import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import PerText from '../../components/custom_components/Text'
import { useAuth } from '../../context/auth-context'
import { MeetingType, FullMeetingFragment } from '../../types'
import { useDateTime } from '../../shared/useDateTime'
import PerSpacer from '../../components/custom_components/Spacer'
import { PerCollapse } from '../../components/custom_components/Collapse'

interface HintsOtherMeeting {
  meeting: FullMeetingFragment
  className?: string
}

export const HintsOtherMeeting: FC<HintsOtherMeeting> = ({ meeting, className = '' }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { isToday } = useDateTime(user)

  const isEmployeePreparation = user.employeeId === meeting.employee.id
  const isReviewerPreparation =
    meeting.reviewer && user.employeeId === meeting.reviewer.id && !(meeting.managerPrepared || isToday(meeting.date))
  const isMeeting = meeting.reviewer && user.employeeId === meeting.reviewer.id && (meeting.managerPrepared || isToday(meeting.date))

  const hintText = (text) => (
    <PerText ml="s" appearance="flowing-text" color="secondary">
      {text}
    </PerText>
  )

  return (
    <>
      {meeting.type === MeetingType.OtherProjectFeedback && (
        <>
          {isEmployeePreparation && (
            <div data-e2e-other-hint-projectfeedback-replyer>
              <PerCollapse
                className={className}
                panels={[
                  {
                    heading: t(`otherMeetings.hints.projectFeedback.replyer.why.headline`),
                    content: hintText(t(`otherMeetings.hints.projectFeedback.replyer.why.description`)),
                  },
                  {
                    heading: t(`otherMeetings.hints.projectFeedback.replyer.how.headline`),
                    content: hintText(t(`otherMeetings.hints.projectFeedback.replyer.how.description`)),
                  },
                ]}
              />
              <PerCollapse
                className={className}
                panels={[
                  {
                    heading: t(`otherMeetings.hints.projectFeedback.meeting.hrSupport.headline`),
                    content: hintText(t(`otherMeetings.hints.projectFeedback.meeting.hrSupport.description`)),
                  },
                ]}
              />
            </div>
          )}
          {isReviewerPreparation && (
            <div data-e2e-other-hint-projectfeedback-reviewer>
              <PerText appearance="flowing-text" color="secondary">
                {t(`otherMeetings.hints.projectFeedback.reviewer.description1`)}
              </PerText>
              <PerText mb="s" appearance="flowing-text" color="secondary">
                {t(`otherMeetings.hints.projectFeedback.reviewer.description2`)}
              </PerText>
              <PerCollapse
                className={className}
                panels={[
                  {
                    heading: t(`otherMeetings.hints.projectFeedback.meeting.hrSupport.headline`),
                    content: hintText(t(`otherMeetings.hints.projectFeedback.meeting.hrSupport.description`)),
                  },
                ]}
              />
            </div>
          )}
          {isMeeting && (
            <div data-e2e-other-hint-projectfeedback-meeting>
              <PerText appearance="flowing-text" color="secondary" mb="m">
                {t(`otherMeetings.hints.projectFeedback.meeting.description`)}
              </PerText>
            </div>
          )}
        </>
      )}
      {meeting.type === MeetingType.OtherGeneric && (
        <>
          {isEmployeePreparation && (
            <div data-e2e-other-hint-general-replyer>
              <PerText appearance="label" color="secondary" mb="xs">
                {t('otherMeetings.hints.otherMeeting.replyer.why.headline')}
              </PerText>
              <PerText appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.otherMeeting.replyer.why.description')}
              </PerText>

              <PerText appearance="label" color="secondary" mt="m" mb="xs">
                {t('otherMeetings.hints.otherMeeting.replyer.how.headline')}
              </PerText>
              <PerText appearance="flowing-text" color="secondary" mb="m">
                {t('otherMeetings.hints.otherMeeting.replyer.how.description')}
              </PerText>
              <PerCollapse
                className={className}
                panels={[
                  {
                    heading: t(`otherMeetings.hints.otherMeeting.meeting.hrSupport.headline`),
                    content: hintText(t(`otherMeetings.hints.otherMeeting.meeting.hrSupport.description`)),
                  },
                ]}
              />
            </div>
          )}
          {isReviewerPreparation && (
            <div data-e2e-other-hint-general-reviewer>
              <PerText appearance="label" color="secondary" mb="xs">
                {t('otherMeetings.hints.otherMeeting.reviewer.content.headline')}
              </PerText>
              <PerText appearance="flowing-text" color="secondary" mb="s">
                {t('otherMeetings.hints.otherMeeting.reviewer.content.description')}
              </PerText>
              <PerCollapse
                className={className}
                panels={[
                  {
                    heading: t(`otherMeetings.hints.otherMeeting.meeting.hrSupport.headline`),
                    content: hintText(t(`otherMeetings.hints.otherMeeting.meeting.hrSupport.description`)),
                  },
                ]}
              />
            </div>
          )}
          {isMeeting && (
            <div data-e2e-other-hint-general-meeting>
              <PerText mb="xs" appearance="label" color="secondary">
                {t('otherMeetings.hints.otherMeeting.meeting.procedure.headline')}
              </PerText>
              <PerText appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.otherMeeting.meeting.procedure.description')}
              </PerText>

              <PerText mt="m" mb="xs" appearance="label" color="secondary">
                {t('otherMeetings.hints.otherMeeting.meeting.remember.headline')}
              </PerText>
              <PerText appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.otherMeeting.meeting.remember.description1')}
              </PerText>
              <PerText appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.otherMeeting.meeting.remember.description2')}
              </PerText>
            </div>
          )}
        </>
      )}
      {meeting.type === MeetingType.OtherProbationTalk && (
        <>
          {isEmployeePreparation && (
            <div data-e2e-other-hint-probationtalk-replyer>
              <PerText mb="xs" appearance="label" color="secondary">
                {t('otherMeetings.hints.probationTalk.replyer.talk.headline')}
              </PerText>
              <PerText mb="m" appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.probationTalk.replyer.talk.description')}
              </PerText>

              <PerText mb="xs" appearance="label" color="secondary">
                {t('otherMeetings.hints.probationTalk.replyer.agenda.headline')}
              </PerText>
              <PerText mb="m" appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.probationTalk.replyer.agenda.description')}
              </PerText>

              <PerCollapse
                className={className}
                panels={[
                  {
                    heading: t(`otherMeetings.hints.probationTalk.replyer.hrInvolved.headline`),
                    content: hintText(t(`otherMeetings.hints.probationTalk.replyer.hrInvolved.description`)),
                  },
                ]}
              />
              <PerCollapse
                className={className}
                panels={[
                  {
                    heading: t(`otherMeetings.hints.probationTalk.meeting.hrSupport.headline`),
                    content: hintText(t(`otherMeetings.hints.probationTalk.meeting.hrSupport.description`)),
                  },
                ]}
              />
            </div>
          )}
          {isReviewerPreparation && (
            <div data-e2e-other-hint-probationtalk-reviewer>
              <PerText mb="xs" appearance="label" color="secondary">
                {t('otherMeetings.hints.probationTalk.reviewer.prep.headline')}
              </PerText>
              <PerText mb="m" appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.probationTalk.reviewer.prep.description')}
              </PerText>

              <PerText mb="xs" appearance="label" color="secondary">
                {t('otherMeetings.hints.probationTalk.reviewer.feedback.headline')}
              </PerText>
              <PerSpacer ml="xs">
                <ul>
                  <li>
                    <PerText mb="xs" appearance="flowing-text" color="secondary">
                      {t('otherMeetings.hints.probationTalk.reviewer.feedback.point1')}
                    </PerText>
                  </li>
                  <li>
                    <PerText mb="xs" appearance="flowing-text" color="secondary">
                      {t('otherMeetings.hints.probationTalk.reviewer.feedback.point2')}
                    </PerText>
                  </li>
                  <li>
                    <PerText mb="xs" appearance="flowing-text" color="secondary">
                      {t('otherMeetings.hints.probationTalk.reviewer.feedback.point3')}
                    </PerText>
                  </li>
                </ul>
              </PerSpacer>
              <PerText mb="m" appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.probationTalk.reviewer.feedback.description')}
              </PerText>

              <PerText mb="xs" appearance="label" color="secondary">
                {t('otherMeetings.hints.probationTalk.reviewer.targets.headline')}
              </PerText>
              <PerText mb="m" appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.probationTalk.reviewer.targets.description')}
              </PerText>

              <PerCollapse
                className={className}
                panels={[
                  {
                    heading: t(`otherMeetings.hints.probationTalk.reviewer.hrSupport.headline`),
                    content: hintText(t(`otherMeetings.hints.probationTalk.reviewer.hrSupport.description`)),
                  },
                ]}
              />
            </div>
          )}

          {isMeeting && (
            <div data-e2e-other-hint-probationtalk-meeting>
              <PerText mb="xs" mt="m" appearance="label" color="secondary">
                {t('otherMeetings.hints.probationTalk.meeting.goals.headline')}
              </PerText>
              <PerText mb="m" appearance="flowing-text" color="secondary">
                {t('otherMeetings.hints.probationTalk.meeting.goals.description')}
              </PerText>
            </div>
          )}
        </>
      )}
    </>
  )
}
